import { Err, Ok, Result } from 'ts-res';
import { KameleoonStorageKey } from 'src/storage';
import { SetDataCallbackParametersType } from 'src/types';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { IVisitorCodeManager } from './types';
import {
  DEFAULT_MAX_AGE,
  PATH,
  VISITOR_CODE_LENGTH,
  VISITOR_CODE_MAX_LENGTH,
} from './constants';
import { KameleoonLogger } from 'src/logging';
import { VariationConfiguration } from "src/variationConfiguration";
import { ForcedFeatureVariationType } from "src/storage/types";

/**
 * VisitorCodeManager - a class which manages visitor codes using cookies, offering functions to generate, retrieve,
 * and handle these codes while considering user consent.
 **/
export class VisitorCodeManager implements IVisitorCodeManager {
  private variationConfiguration?: VariationConfiguration;
  private isConsentRequired: boolean = false;
  private setDataCallback?: (params: SetDataCallbackParametersType) => void;
  private getDataCallback?: (key: string) => string | null;

  set consentRequired(isRequired: boolean) {
    this.isConsentRequired = isRequired;
  }

  get consentRequired(): boolean {
    return this.isConsentRequired;
  }

  set getData(callback: (key: string) => string) {
    this.getDataCallback = callback;
  }

  set setData(callback: (params: SetDataCallbackParametersType) => void) {
    this.setDataCallback = callback;
  }

  get setData(): (params: SetDataCallbackParametersType) => void {
    if (this.setDataCallback) {
      return this.setDataCallback;
    }

    throw new Error('Missing setDataCallback!');
  }

  public validateVisitorCode(
    visitorCode: string,
  ): Result<void, KameleoonError> {
    if (visitorCode.length === 0) {
      return Err(new KameleoonError(KameleoonException.VisitorCodeEmpty));
    }

    if (visitorCode.length > VISITOR_CODE_MAX_LENGTH) {
      return Err(new KameleoonError(KameleoonException.VisitorCodeMaxLength));
    }

    return Ok();
  }

  public getVisitorCode(defaultVisitorCode?: string): string {
    KameleoonLogger.debug`CALL: VisitorCodeManager.getVisitorCode(defaultVisitorCode: ${defaultVisitorCode})`;
    if (!this.getDataCallback || !this.setDataCallback) {
      throw new Error(
        'Missing getDataCallback or setDataCallback methods in getVisitorCode!',
      );
    }

    let visitorCode;

    const existingVisitorCode = this.getDataCallback(
      KameleoonStorageKey.VisitorCode,
    );

    if (existingVisitorCode) {
      visitorCode = existingVisitorCode;
    }

    if (!visitorCode) {
      if (defaultVisitorCode) {
        this.validateVisitorCode(defaultVisitorCode).throw();
        visitorCode = defaultVisitorCode;
        KameleoonLogger.debug`Used default visitor code: ${visitorCode}`;
      } else {
        visitorCode = this.generateVisitorCode();
        KameleoonLogger.debug`Generated new visitor code: ${visitorCode}`;
      }
    }

    this.processSimulatedVariations(visitorCode);

    if (!this.isConsentRequired) {
      this.setDataCallback({
        visitorCode,
        key: KameleoonStorageKey.VisitorCode,
        maxAge: DEFAULT_MAX_AGE,
        path: PATH,
      });
    }

    KameleoonLogger.debug`RETURN: VisitorCodeManager.getVisitorCode(defaultVisitorCode: ${defaultVisitorCode}) -> (visitorCode: ${visitorCode})`;
    return visitorCode;
  }

  private processSimulatedVariations(visitorCode: string): void {
    if (this.variationConfiguration) {
      const simulatedVariationsData = this.getSimulatedVariations(visitorCode);
      try {
        this.variationConfiguration.clearSimulatedVariations(visitorCode).throw();
        if (simulatedVariationsData !== null && simulatedVariationsData.length > 0) {
          this.variationConfiguration.updateForcedFeatureVariations(visitorCode, simulatedVariationsData).throw();
        }
      } catch (error) {
        if (error instanceof Error) {
          KameleoonLogger.error`Failed to process simulated variations: ${error.message}`;
        }
      }
    }
  }

  private getSimulatedVariations(
    visitorCode: string,
  ): ForcedFeatureVariationType[] | null {
    KameleoonLogger.debug`CALL: VisitorCodeManager.getSimulatedVariations(visitorCode: ${visitorCode})`;
    const simulatedVariationsData = this.getDataCallback!(
      KameleoonStorageKey.KameleoonSimulationFFData,
    );

    let variations: ForcedFeatureVariationType[] | null = null;
    if (
      typeof simulatedVariationsData === 'string' &&
      simulatedVariationsData.length > 0
    ) {
      try {
        const parsed = JSON.parse(decodeURIComponent(simulatedVariationsData));
        if (parsed && typeof parsed === 'object') {
          variations = Object.entries(parsed)
            .map(([key, value]) => {
              return this.variationConfiguration!.simulatedVariationFromJson(key, value);
            })
            .filter((variation) => variation !== null) as ForcedFeatureVariationType[];
        }
      } catch (error) {
        if (error instanceof Error) {
          KameleoonLogger.error`Failed to parse simulated variations: ${error.message}`;
        }
      }
    }
    KameleoonLogger.debug`RETURN: VisitorCodeManager.getSimulatedVariations(visitorCode: ${visitorCode}) -> (variations: ${variations})`;
    return variations;
  }

  private generateVisitorCode(): string {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const randomCharacterArray: string[] = [];

    for (let i = 0; i < VISITOR_CODE_LENGTH; i++) {
      randomCharacterArray.push(
        characters[Math.floor(Math.random() * characters.length)],
      );
    }

    return randomCharacterArray.join('');
  }

  public setVariationConfiguration(variationConfiguration: VariationConfiguration): void {
    if (!this.variationConfiguration) {
      this.variationConfiguration = variationConfiguration;
    }
  }
}
